import { useState, useEffect, useCallback } from 'react';
import { Event } from '@/types/events';
import { validateEvent } from '../utils/validation';
import { calculateBudgetMetrics } from '../utils/metrics';
import { eventStorage } from '../services/eventStorage';
import { mockEvents } from '../data/mockEvents';
import { z } from 'zod';

export const INITIAL_EVENT_DATA: Partial<Event> = {
  title: '',
  description: '',
  objectives: [],
  targetGrades: [],
  startDate: new Date().toISOString(),
  endDate: new Date().toISOString(),
  locationType: 'physical',
  location: '',
  virtualLink: '',
  eventCategory: 'workshop',
  status: 'draft',
  eventResources: [],
  staffAssignments: [],
  frequencyType: 'one_time',
  occurrences: [],
  budget: [],
  tasks: [],
  resources: [],
  feedback: [],
  compliance: [],
  metrics: {
    attendance: 0,
    satisfaction: 0,
    budget: {
      estimated: 0,
      actual: null,
      variance: 0
    }
  }
};

export const useEvents = () => {
  const [events, setEvents] = useState<Event[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadEvents = () => {
      try {
        setIsLoading(true);
        // Get events from storage - initialization is handled by storage service
        const events = eventStorage.getEvents();
        setEvents(events);
        setError(null);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load events';
        setError(errorMessage);
        console.error('Error loading events:', err);
      } finally {
        setIsLoading(false);
      }
    };

    loadEvents();
  }, []);

  const addEvent = useCallback(async (eventData: Partial<Event>) => {
    try {
      const validatedEvent = validateEvent(eventData);
      const metrics = calculateBudgetMetrics(validatedEvent);
      validatedEvent.metrics.budget = metrics;

      const savedEvent = eventStorage.saveEvent(validatedEvent);
      if (!savedEvent) {
        throw new Error('Failed to save event');
      }

      setEvents(prev => [...prev, savedEvent]);
      setError(null);
      return savedEvent;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to add event';
      setError(errorMessage);
      console.error('Error adding event:', err);
      return null;
    }
  }, []);

  const updateEvent = useCallback(async (eventData: Event) => {
    try {
      const validatedEvent = validateEvent(eventData);
      const savedEvent = eventStorage.updateEvent(validatedEvent);

      if (!savedEvent) {
        throw new Error('Failed to save event changes to storage');
      }

      setEvents(prev => prev.map(event => 
        event.id === savedEvent.id ? savedEvent : event
      ));
      setError(null);
      return savedEvent;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred while updating the event';
      setError(errorMessage);
      console.error('Error updating event:', err);
      return null;
    }
  }, []);

  const updateEventTasks = useCallback(async (eventId: string, tasks: Event['tasks']) => {
    try {
      const updatedEvent = eventStorage.updateEventTasks(eventId, tasks);
      if (!updatedEvent) {
        throw new Error('Failed to save task changes to storage');
      }

      setEvents(prev => prev.map(event => 
        event.id === eventId ? updatedEvent : event
      ));
      setError(null);
      return updatedEvent;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred while updating tasks';
      setError(errorMessage);
      console.error('Error updating event tasks:', err);
      return null;
    }
  }, []);

  const deleteEvent = useCallback(async (id: string) => {
    try {
      const success = eventStorage.deleteEvent(id);
      if (!success) {
        throw new Error('Failed to delete event');
      }

      setEvents(prev => prev.filter(event => event.id !== id));
      setError(null);
      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete event';
      setError(errorMessage);
      console.error('Error deleting event:', err);
      return false;
    }
  }, []);

  return {
    events,
    isLoading,
    error,
    addEvent,
    updateEvent,
    updateEventTasks,
    deleteEvent
  };
};